import { defineStore } from 'pinia';
import { Config } from '@/config';
import apiService from '@/services/api.service';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import type { SavingOptions } from '@/services/types/file-uploader.types';

import type { ContactListMemberDto } from '@/services/types/api.types';

export interface ContactListState {
  contactListMembers: ContactListMemberDto[];
  itemLoadingStates: Set<number>;
}

export const useContactListMembersStore = defineStore({
  id: 'contactListMembers',
  state: (): ContactListState => ({
    contactListMembers: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (projectModuleId: number) => state.itemLoadingStates.has(projectModuleId),
    orderedContactListMembers: (state) => {
      return [...state.contactListMembers].sort((a, b) => a.order_num - b.order_num);
    },
  },
  actions: {
    async fetchContactListMembersByModuleId(projectModuleId: number) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<ContactListMemberDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/contact-list-members`,
        );
        this.contactListMembers = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },
    createContactListMember(projectModuleId: number): ContactListMemberDto {
      return {
        project_module_id: projectModuleId,
        name: { en: '', fr: '' },
        role: { en: '', fr: '' },
        organization: { en: '', fr: '' },
        description: { en: '', fr: '' },
        email: null,
        phone: null,
        profile_image: null,
        socials: {
          facebook: null,
          linkedIn: null,
          x: null,
          instagram: null,
        },
        order_num: -1,
      };
    },
    async saveContactListMember(contactListMember: ContactListMemberDto, options?: SavingOptions) {
      try {
        if (options?.version === 'v2' && options.metadata) {
          const url = contactListMember.id
            ? `${Config.api.v2}/contact-list-members/${contactListMember.id}`
            : `${Config.api.v2}/contact-list-members`;

          await FileUploaderFactory.create(options.version).upload({
            data: contactListMember,
            files: options.files,
            metadata: options.metadata,
            url,
          });
        } else if (contactListMember.id) {
          await apiService.put<ContactListMemberDto>(
            `${Config.api.v2}/contact-list-members/${contactListMember.id}`,
            contactListMember,
          );
        } else {
          await apiService.post<ContactListMemberDto>(`${Config.api.v2}/contact-list-members`, contactListMember);
        }
        await this.fetchContactListMembersByModuleId(contactListMember.project_module_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteContactListMember(contactListMember: ContactListMemberDto) {
      try {
        await apiService.delete(`${Config.api.v2}/contact-list-members/${contactListMember.id!}`);
        await this.fetchContactListMembersByModuleId(contactListMember.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },
    async updateContactListMembers(...contactListMembers: ContactListMemberDto[]) {
      try {
        for (const contactListMember of contactListMembers) {
          await apiService.put(`${Config.api.v2}/contact-list-members/${contactListMember.id}`, contactListMember);
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
});
